import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Card, Dropdown, Space, Spin, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  DownOutlined,
  EnvironmentOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MailOutlined,
  SettingOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type {
  ReportDefinition,
  ReportFilterOption,
  ReportFormat,
  ReportRun,
  ReportRunResult,
} from '@goelectric/shared-types';
import { reportesApi } from '../api/reportesApi';
import {
  defaultColumnSelection,
  ReportColumnsModal,
  type ColumnSelection,
} from '../components/ReportColumnsModal';
import { ReportEmailModal } from '../components/ReportEmailModal';
import { ReportFiltersForm } from '../components/ReportFiltersForm';
import { ReportPreviewTable } from '../components/ReportPreviewTable';
import { ReportRunsPanel } from '../components/ReportRunsPanel';
import { useCatalogoOrigenes } from '../hooks/useCatalogoOrigenes';
import { useReportRuns } from '../hooks/useReportRuns';
import type { ReportEmailValues } from '../utils/email';
import {
  artifactErrorMessage,
  dropFiltersForHiddenColumns,
  pruneFilters,
  reportErrorMessage,
  type ReportFilterValues,
} from '../utils/format';

/** Filas por página de la vista previa (el contrato acepta hasta 100). */
const PREVIEW_LIMIT = 20;

/** Espera antes de ejecutar la vista previa tras un cambio de filtro/columna. */
const PREVIEW_DEBOUNCE_MS = 400;

type FileReportFormat = Exclude<ReportFormat, 'json'>;

const FILE_FORMATS: FileReportFormat[] = ['csv', 'xlsx', 'pdf'];

const FORMAT_LABELS: Record<ReportFormat, string> = {
  json: 'Vista previa',
  csv: 'CSV',
  xlsx: 'Excel',
  pdf: 'PDF',
};

const FORMAT_ICONS: Record<FileReportFormat, ReactNode> = {
  csv: <FileTextOutlined />,
  xlsx: <FileExcelOutlined />,
  pdf: <FilePdfOutlined />,
};

/**
 * Icono de la tarjeta por la clave estable `ReportDefinition.icono`, no por `id`.
 * Agregar un reporte no obliga a tocar la pantalla; una clave desconocida cae en
 * el icono genérico.
 */
const REPORT_ICONS: Record<NonNullable<ReportDefinition['icono']>, ReactNode> = {
  aliados: <ShopOutlined />,
  ubicaciones: <EnvironmentOutlined />,
  cargadores: <ThunderboltOutlined />,
  usuarios: <UserOutlined />,
  generico: <FileTextOutlined />,
};

function reportIcon(icono: ReportDefinition['icono']): ReactNode {
  return REPORT_ICONS[icono ?? 'generico'] ?? REPORT_ICONS.generico;
}

/** Filtros y columnas que ya se aplicaron a la última ejecución. */
interface AppliedQuery {
  filtros: ReportFilterValues;
  columnas: string[];
}

const EMPTY_QUERY: AppliedQuery = { filtros: {}, columnas: [] };

/**
 * Pantalla de Reportes (US-2.8, `/reportes`).
 *
 * Todo se renderiza desde la definición que devuelve el backend: el catálogo
 * lista lo que el rol puede ver, y el formulario y la tabla se generan desde
 * `columnas[]` (la filtrabilidad es una propiedad de la columna). No hay
 * reportes, filtros ni columnas hardcodeados.
 */
export function ReportsPage() {
  const [catalog, setCatalog] = useState<ReportDefinition[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [definition, setDefinition] = useState<ReportDefinition | null>(null);
  const [definitionLoading, setDefinitionLoading] = useState(false);
  const [definitionError, setDefinitionError] = useState<string | null>(null);

  const [filterValues, setFilterValues] = useState<ReportFilterValues>({});
  /** Columnas con su selección; siempre en el orden declarado. */
  const [columnSelection, setColumnSelection] = useState<ColumnSelection[]>([]);
  const [columnsOpen, setColumnsOpen] = useState(false);

  const [preview, setPreview] = useState<ReportRunResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  /** Última consulta ejecutada: la descarga usa esto, no lo que se está tipeando. */
  const [applied, setApplied] = useState<AppliedQuery>(EMPTY_QUERY);

  const [downloading, setDownloading] = useState<Set<ReportFormat>>(new Set());
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingArtifactId, setDownloadingArtifactId] = useState<string | null>(null);

  const [emailOpen, setEmailOpen] = useState(false);
  /** Run que se reenvía; `null` = envío nuevo con los filtros aplicados. */
  const [emailRun, setEmailRun] = useState<ReportRun | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const {
    runs,
    total: runsTotal,
    hasMore: runsHasMore,
    loading: runsLoading,
    loadingMore: runsLoadingMore,
    error: runsError,
    pollError: runsPollError,
    refresh: refreshRuns,
    loadMore: loadMoreRuns,
    registerAccepted,
  } = useReportRuns(selectedId);

  /** Cancela la ejecución anterior; una vista previa vieja no debe pisar la nueva. */
  const abortRef = useRef<AbortController | null>(null);
  /** Reporte cuya vista previa ya se disparó: su primera carga va sin debounce. */
  const lastReportIdRef = useRef<string | null>(null);

  const { message } = App.useApp();

  /** Título del reporte para el historial (la ejecución sólo guarda el `reporteId`). */
  const reportTitles = useMemo(() => {
    const titles: Record<string, string> = {};
    for (const report of catalog) titles[report.id] = report.titulo;
    if (definition) titles[definition.id] = definition.titulo;
    return titles;
  }, [catalog, definition]);

  /** Orígenes de catálogo declarados por las columnas (sin repetir). */
  const origenes = useMemo(
    () => [
      ...new Set(
        (definition?.columnas ?? []).flatMap((columna) =>
          columna.origen ? [columna.origen] : [],
        ),
      ),
    ],
    [definition],
  );
  const catalogos = useCatalogoOrigenes(origenes);

  const loadCatalog = useCallback(() => {
    setCatalogLoading(true);
    setCatalogError(null);
    reportesApi
      .list()
      .then(setCatalog)
      .catch((error: unknown) => {
        setCatalogError(reportErrorMessage(error, 'No se pudieron cargar los reportes.'));
      })
      .finally(() => setCatalogLoading(false));
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // Al elegir un reporte se pide su definición completa y se reinicia la vista.
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setDefinitionLoading(true);
    setDefinitionError(null);
    setDefinition(null);
    setPreview(null);
    setPreviewError(null);
    setDownloadError(null);
    setApplied(EMPTY_QUERY);
    setPage(1);
    setEmailOpen(false);
    setEmailRun(null);
    setEmailError(null);

    reportesApi
      .getDefinition(selectedId)
      .then((next) => {
        if (cancelled) return;
        setDefinition(next);
        setFilterValues({});
        setColumnSelection(defaultColumnSelection(next.columnas));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDefinitionError(
          reportErrorMessage(error, 'No se pudo cargar la definición del reporte.'),
        );
      })
      .finally(() => {
        if (!cancelled) setDefinitionLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  /** Columnas que viajan al `run` y a la descarga, en orden declarado. */
  const selectedColumns = columnSelection
    .filter((columna) => columna.seleccionada)
    .map((columna) => columna.clave);
  const hasColumns = selectedColumns.length > 0;

  /**
   * La filtrabilidad es una propiedad de la columna: se muestran las columnas
   * seleccionadas que declaran `filtro`, en el orden declarado.
   */
  const filtrableColumns = useMemo(() => {
    const seleccionadas = new Set(selectedColumns);
    return (definition?.columnas ?? []).filter(
      (columna) => columna.filtro !== undefined && seleccionadas.has(columna.clave),
    );
  }, [definition, selectedColumns]);

  /**
   * Opciones para la tabla: los catálogos por `origen` se cargan por columna.
   * Los estáticos ya salen de `columna.opciones`.
   */
  const catalogoOpciones = useMemo(() => {
    const result: Record<string, ReportFilterOption[]> = {};
    for (const columna of definition?.columnas ?? []) {
      if (!columna.origen) continue;
      const opciones = catalogos.opciones[columna.origen];
      if (opciones) result[columna.clave] = opciones;
    }
    return result;
  }, [definition, catalogos.opciones]);

  /**
   * Al desmarcar una columna se limpia su filtro activo: nada de filtros
   * invisibles aplicándose. Se avisa con un mensaje breve, no con un modal.
   */
  const handleColumnSelectionChange = useCallback(
    (next: ColumnSelection[]) => {
      setColumnSelection(next);
      if (!definition) return;

      const seleccionadas = next
        .filter((columna) => columna.seleccionada)
        .map((columna) => columna.clave);
      const { filtros: limpios, removidos } = dropFiltersForHiddenColumns(
        filterValues,
        definition.columnas,
        seleccionadas,
      );
      if (removidos.length === 0) return;

      setFilterValues(limpios);

      const titulos = removidos.map((clave) => {
        const columna = definition.columnas.find((candidata) => candidata.clave === clave);
        return `"${columna?.titulo ?? clave}"`;
      });
      message.info(
        removidos.length === 1
          ? `Se quitó el filtro ${titulos[0]} porque desmarcaste su columna.`
          : `Se quitaron ${removidos.length} filtros de columnas desmarcadas.`,
      );
    },
    [definition, filterValues, message],
  );

  /**
   * Ejecuta la vista previa inmediatamente. Cancela la petición previa: una
   * cancelación es esperada y no se muestra como error.
   */
  const runPreview = useCallback(
    (targetPage: number) => {
      if (!definition) return;
      const columnas = columnSelection
        .filter((columna) => columna.seleccionada)
        .map((columna) => columna.clave);
      if (columnas.length === 0) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const filtros = pruneFilters(filterValues);
      setApplied({ filtros, columnas });
      setPage(targetPage);
      setPreviewLoading(true);
      setPreviewError(null);

      reportesApi
        .run(definition.id, {
          filtros,
          columnas,
          formato: 'json',
          page: targetPage,
          limit: PREVIEW_LIMIT,
          signal: controller.signal,
        })
        .then((result) => {
          if (controller.signal.aborted) return;
          setPreview(result);
        })
        .catch((error: unknown) => {
          // Abortar la petición anterior no es un error para el usuario.
          if (controller.signal.aborted) return;
          setPreview(null);
          setPreviewError(reportErrorMessage(error, 'No se pudo ejecutar el reporte.'));
        })
        .finally(() => {
          if (!controller.signal.aborted) setPreviewLoading(false);
        });
    },
    [definition, filterValues, columnSelection],
  );

  // Vista previa automática: debounce de 400 ms ante filtros/columnas; la primera
  // carga de un reporte es inmediata. Sin columnas no se ejecuta.
  useEffect(() => {
    if (!definition) return;

    if (!hasColumns) {
      abortRef.current?.abort();
      abortRef.current = null;
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
      return;
    }

    const changedReport = lastReportIdRef.current !== definition.id;
    lastReportIdRef.current = definition.id;

    if (changedReport) {
      runPreview(1);
      return;
    }

    const timer = setTimeout(() => runPreview(1), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [definition, hasColumns, runPreview]);

  // Al desmontar, cancelar cualquier vista previa en vuelo.
  useEffect(() => () => abortRef.current?.abort(), []);

  /**
   * Pide un archivo. Si el backend responde 202, la ejecución queda en
   * background: se registra en el panel, se refresca el historial y se avisa
   * sin mostrarlo como error.
   */
  const download = useCallback(
    async (
      formato: ReportFormat,
      query?: { reporteId: string; filtros: ReportFilterValues; columnas: string[] },
    ) => {
      const reporteId = query?.reporteId ?? definition?.id;
      const filtros = query?.filtros ?? applied.filtros;
      const columnas = query?.columnas ?? applied.columnas;
      if (!reporteId || columnas.length === 0) return;

      setDownloading((prev) => new Set(prev).add(formato));
      setDownloadError(null);
      try {
        const result = await reportesApi.download(reporteId, { filtros, columnas, formato });
        if (result.tipo === 'aceptado') {
          registerAccepted(result.run, { reporteId, formato, filtros, columnas });
          refreshRuns();
          message.info(
            'El reporte se está generando; aparecerá en Descargas recientes.',
          );
        }
      } catch (error: unknown) {
        setDownloadError(reportErrorMessage(error, 'No se pudo descargar el archivo.'));
      } finally {
        setDownloading((prev) => {
          const next = new Set(prev);
          next.delete(formato);
          return next;
        });
      }
    },
    [definition, applied, registerAccepted, refreshRuns, message],
  );

  const onDownloadMenuClick: MenuProps['onClick'] = ({ key }) => {
    void download(key as ReportFormat);
  };

  /** Descarga el artefacto de una ejecución `LISTO` (409/410/404 con mensajes propios). */
  const downloadArtifact = useCallback(async (run: ReportRun) => {
    setDownloadingArtifactId(run.id);
    setDownloadError(null);
    try {
      await reportesApi.downloadArtifact(run);
    } catch (error: unknown) {
      setDownloadError(artifactErrorMessage(error));
    } finally {
      setDownloadingArtifactId(null);
    }
  }, []);

  /**
   * Vuelve a generar una ejecución fallida con sus parámetros originales. Es una
   * ejecución nueva, no un reintento del mismo run.
   */
  const regenerate = useCallback(
    (run: ReportRun) => {
      void download(run.formato, {
        reporteId: run.reporteId,
        filtros: run.filtros,
        columnas: run.columnas,
      });
    },
    [download],
  );

  /** Datos del reenvío: misma configuración del run, en una ejecución nueva. */
  const emailInitial = useMemo(
    () =>
      emailRun
        ? {
            destinatarios: emailRun.destinatarios ?? [],
            formato: emailRun.formato,
            asunto: emailRun.asunto,
          }
        : null,
    [emailRun],
  );

  /** Abre el modal en blanco (envío nuevo) o pre-cargado (reenvío). */
  const openEmailModal = useCallback((run: ReportRun | null) => {
    setEmailRun(run);
    setEmailError(null);
    setEmailOpen(true);
  }, []);

  /**
   * Encola el envío por correo. Con el reporte seleccionado usa los filtros y
   * columnas aplicados; en un reenvío, los de la ejecución original. La
   * respuesta es siempre 202 y el run se registra en el panel de descargas.
   */
  const sendByEmail = useCallback(
    async (values: ReportEmailValues, run: ReportRun | null) => {
      const reporteId = run?.reporteId ?? definition?.id;
      const filtros = run?.filtros ?? applied.filtros;
      const columnas = run?.columnas ?? applied.columnas;
      if (!reporteId || columnas.length === 0) return;

      setSendingEmail(true);
      setEmailError(null);
      try {
        const accepted = await reportesApi.sendByEmail(reporteId, {
          filtros,
          columnas,
          formato: values.formato,
          destinatarios: values.destinatarios,
          asunto: values.asunto || undefined,
        });
        registerAccepted(accepted, {
          reporteId,
          formato: values.formato,
          filtros,
          columnas,
          entrega: 'correo',
          destinatarios: values.destinatarios,
          asunto: values.asunto || null,
        });
        refreshRuns();
        setEmailOpen(false);
        setEmailRun(null);
        message.info('El reporte se está generando y se enviará por correo.');
      } catch (error: unknown) {
        setEmailError(reportErrorMessage(error, 'No se pudo enviar el reporte por correo.'));
      } finally {
        setSendingEmail(false);
      }
    },
    [definition, applied, registerAccepted, refreshRuns, message],
  );

  /** Formatos de archivo que soporta la definición (el correo nunca manda `json`). */
  const emailFormatos = definition
    ? FILE_FORMATS.filter((formato) => definition.formatos.includes(formato))
    : [];

  const downloadItems: MenuProps['items'] = definition
    ? FILE_FORMATS.filter((formato) => definition.formatos.includes(formato)).map((formato) => ({
        key: formato,
        label: FORMAT_LABELS[formato],
        icon: downloading.has(formato) ? <Spin size="small" /> : FORMAT_ICONS[formato],
      }))
    : [];

  return (
    <div className="reportes-page">
      <div className="reportes-page__header">
        <Typography.Title level={3} style={{ margin: 0 }}>
          Reportes
        </Typography.Title>
      </div>

      {catalogError ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los reportes"
          description={catalogError}
          action={
            <Button size="small" onClick={loadCatalog}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Spin spinning={catalogLoading}>
          {catalog.length === 0 && !catalogLoading ? (
            <Typography.Text type="secondary">
              No hay reportes disponibles para tu rol.
            </Typography.Text>
          ) : (
            <div className="reportes-card-grid">
              {catalog.map((report) => (
                <Card
                  key={report.id}
                  size="small"
                  className={`reportes-card${
                    selectedId === report.id ? ' reportes-card--selected' : ''
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Abrir ${report.titulo}`}
                  aria-pressed={selectedId === report.id}
                  onClick={() => setSelectedId(report.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedId(report.id);
                    }
                  }}
                >
                  <div className="reportes-card__content">
                    <span className="reportes-card__icon">{reportIcon(report.icono)}</span>
                    <Typography.Text strong>{report.titulo}</Typography.Text>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Spin>
      )}

      {definitionError && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la definición"
          description={definitionError}
        />
      )}

      {selectedId && definitionLoading && (
        <Card>
          <Spin />
        </Card>
      )}

      {definition && (
        <Card
          title={definition.titulo}
          extra={
            <Space>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setColumnsOpen(true)}
                disabled={!hasColumns}
              >
                Columnas ({selectedColumns.length}/{definition.columnas.length})
              </Button>
              <Dropdown
                menu={{ items: downloadItems, onClick: onDownloadMenuClick }}
                trigger={['click']}
                disabled={!hasColumns}
              >
                <Button type="primary" disabled={!hasColumns}>
                  Descargar <DownOutlined />
                </Button>
              </Dropdown>
              <Button
                icon={<MailOutlined />}
                onClick={() => openEmailModal(null)}
                disabled={!hasColumns || emailFormatos.length === 0}
              >
                Enviar por correo
              </Button>
            </Space>
          }
        >
          <ReportFiltersForm
            columnas={filtrableColumns}
            values={filterValues}
            onChange={setFilterValues}
            catalogos={catalogos}
          />

          {!hasColumns && (
            <Typography.Text type="warning" style={{ display: 'block', marginBottom: 16 }}>
              Seleccioná al menos una columna para ejecutar el reporte.
            </Typography.Text>
          )}

          {downloadError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="No se pudo descargar el archivo"
              description={downloadError}
            />
          )}

          <ReportPreviewTable
            definition={definition}
            visibleColumns={selectedColumns}
            catalogoOpciones={catalogoOpciones}
            result={preview}
            loading={previewLoading}
            error={previewError}
            page={page}
            limit={PREVIEW_LIMIT}
            onPageChange={runPreview}
            onRetry={() => runPreview(page)}
          />

          <ReportRunsPanel
            runs={runs}
            reportTitles={reportTitles}
            total={runsTotal}
            hasMore={runsHasMore}
            loading={runsLoading}
            loadingMore={runsLoadingMore}
            error={runsError}
            pollError={runsPollError}
            downloadingId={downloadingArtifactId}
            onRefresh={refreshRuns}
            onLoadMore={loadMoreRuns}
            onDownload={(run) => void downloadArtifact(run)}
            onRegenerate={regenerate}
            onResend={openEmailModal}
          />

          <ReportColumnsModal
            open={columnsOpen}
            columnas={definition.columnas}
            value={columnSelection}
            onChange={handleColumnSelectionChange}
            onClose={() => setColumnsOpen(false)}
          />

          <ReportEmailModal
            open={emailOpen}
            definition={definition}
            initial={emailInitial}
            sending={sendingEmail}
            error={emailError}
            onSend={(values) => void sendByEmail(values, emailRun)}
            onClose={() => setEmailOpen(false)}
          />
        </Card>
      )}
    </div>
  );
}
